import { cn } from '@/lib/utils/cn';
import { formatCents, formatCentsAsK, spendPosture, type AiCostSummary } from '@/lib/api/ai-costs';

/**
 * Firm-level AI spend meter (T-0112 / C20-122).
 *
 * Mirrors the prototype's `screens-soft-gaps.js → firmAiCosts()` top
 * card: a large headline figure, a horizontal meter coloured by posture
 * (>90% critical / >75% at-risk / otherwise healthy), and a vertical
 * "today" marker at the pro-rated point of the period. Renders pure
 * derivations from the AiCostSummary — every figure is computed inside.
 *
 * Posture rollup (README §8 pattern 6): meter color reflects the spend
 * posture which is computed, not stored. The user does not edit posture.
 */
export interface AiCostMeterProps {
  summary: AiCostSummary;
}

const POSTURE_BG: Record<'healthy' | 'at-risk' | 'critical', string> = {
  healthy: 'bg-posture-healthy',
  'at-risk': 'bg-posture-at-risk',
  critical: 'bg-posture-critical',
};

const POSTURE_TEXT: Record<'healthy' | 'at-risk' | 'critical', string> = {
  healthy: 'text-posture-healthy',
  'at-risk': 'text-posture-at-risk',
  critical: 'text-posture-critical',
};

const POSTURE_TINT: Record<'healthy' | 'at-risk' | 'critical', string> = {
  healthy: 'bg-posture-healthy-tint',
  'at-risk': 'bg-posture-at-risk-tint',
  critical: 'bg-posture-critical-tint',
};

export function AiCostMeter({ summary }: AiCostMeterProps) {
  const pct = summary.firm_cap_cents > 0 ? summary.firm_mtd_cents / summary.firm_cap_cents : 0;
  const posture = spendPosture(summary.firm_mtd_cents, summary.firm_cap_cents);
  const dayPct =
    summary.billing_period_days_total > 0
      ? summary.billing_period_days_elapsed / summary.billing_period_days_total
      : 0;
  // Linear projection of MTD spend out to the period end.
  const projectedCents =
    summary.billing_period_days_elapsed === 0
      ? 0
      : (summary.firm_mtd_cents / summary.billing_period_days_elapsed) *
        summary.billing_period_days_total;
  const delta =
    summary.firm_last_month_cents > 0
      ? ((summary.firm_mtd_cents - summary.firm_last_month_cents) / summary.firm_last_month_cents) *
        100
      : 0;
  const hardCeilingCents = Math.round(summary.firm_cap_cents * 1.15);

  return (
    <section
      aria-labelledby="ai-cost-meter-heading"
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 id="ai-cost-meter-heading" className="text-h3 font-semibold text-ink-900">
            Firm monthly budget
          </h2>
          <p className="text-meta text-ink-500">
            Period {summary.billing_period_start} · {(dayPct * 100).toFixed(0)}% of month elapsed
          </p>
        </div>
        <span
          className={cn(
            'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-meta font-medium',
            POSTURE_TINT[posture],
            POSTURE_TEXT[posture],
          )}
        >
          {(pct * 100).toFixed(1)}% used
        </span>
      </div>

      <div className="mt-3 flex items-baseline gap-3">
        <span
          className="text-display font-semibold text-ink-900 tabular-nums"
          style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
        >
          {formatCents(summary.firm_mtd_cents)}
        </span>
        <span className="text-body-sm text-ink-500">
          of {formatCents(summary.firm_cap_cents)} cap · projected {formatCentsAsK(projectedCents)}{' '}
          for full month
        </span>
      </div>

      <div
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(100, pct * 100)}
        aria-label={`Firm AI spend ${(pct * 100).toFixed(1)}% of cap`}
        className="relative mt-4 h-3 overflow-visible rounded-full bg-ink-100"
      >
        <div
          aria-hidden="true"
          className={cn(
            'absolute left-0 top-0 h-full rounded-full transition-[width]',
            POSTURE_BG[posture],
          )}
          style={{ width: `${Math.min(100, pct * 100)}%` }}
        />
        {/* "Today" pro-rated marker — vertical line + label. */}
        <div
          aria-hidden="true"
          className="absolute -top-1 bottom-[-1px] w-px bg-accent-amber-600"
          style={{ left: `${dayPct * 100}%` }}
        />
        <span
          aria-hidden="true"
          className="absolute -top-5 -translate-x-1/2 font-mono text-meta text-accent-amber-700"
          style={{ left: `${dayPct * 100}%` }}
        >
          today
        </span>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-body-sm">
        <span className="text-ink-500">
          {delta > 0 ? '+' : ''}
          {delta.toFixed(1)}% vs. last month at same pace
        </span>
        <span className="text-ink-500">
          Hard ceiling: <span className="font-mono">{formatCents(hardCeilingCents)}</span>{' '}
          (auto-throttle)
        </span>
      </div>
    </section>
  );
}
